import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import { Country, State, City } from "country-state-city";
import useEncryption from "../../../services/useEncryption";
import { stepSixSchema } from "../../../services/formValidator/providerSignupValidator";
import {
  createAuditLogs,
  getContactInfo,
  submitContactInfo,
  updateArchiveStatus,
} from "../../../api/providerApi/providerApi";
import { toast } from "react-toastify";
import path from "../../../path";
import useHeaders from "../../../helpers/useHeaders";
import { useSelector } from "react-redux";
import { selectLoc } from "../../../redux/slice/locSlice";
import { Autocomplete, Chip, FormControlLabel, Switch } from "@mui/material";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

export const Stepsix = ({ providerId }) => {
  const { encryptData, decryptData } = useEncryption();
  const headers = useHeaders();
  const { loc } = useSelector(selectLoc);
  const [initialData, setInitialData] = useState(null);
  const [status, setStatus] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [practiceLogoFilename, setPracticeLogoFilename] = useState("");
  const [locationStatus, setLocationStatus] = useState(false);
  const [aboutMe, setAbout] = useState("");
  // const [enableField, setEnableFields] = useState(true);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [medicalInsuranceStatus, setMedicalInsuranceStatus] = useState(false);
  const [buttonText, setButtonText] = useState("Saved");
  const [fetchedFilename, setFetchedFilename] = useState("");
  const [uploadedFilename, setUploadedFilename] = useState("");
  const [stateName, setStateName] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [cityName, setCityName] = useState("");

  const getContactData = async () => {
    try {
      const response = await getContactInfo(providerId);
      const decryptedData = decryptData(
        response?.data?.data?.practiceContactInfo
      );
      const statusData = decryptData(response?.data?.data?.providerStatus);
      const doctorData = decryptData(response?.data?.data?.primaryDoctor);

      setInitialData(decryptedData);
      setStatus(statusData);
      setDoctor(doctorData);
      setIsArchived(statusData?.isArchived || false);
    } catch (error) {}
  };

  useEffect(() => {
    getContactData();
  }, [providerId]);

  const handleArchiveStatusChange = async (newStatus) => {
    try {

      const encryptedData = encryptData({
        isArchived: newStatus,
      });
      
      const response = await updateArchiveStatus(providerId, encryptedData);

      if (response.status == false) {
        toast.error(response.message[0]);
        // Revert the toggle if update fails
        setIsArchived(!newStatus);
      } else {
        let obj = {
          event: `SuperAdmin has ${newStatus ? 'archived' : 'unarchived'} the location ${loc.organization_name}.`,
        };

        await createAuditLogs({ data: encryptData(obj) }, headers);
        toast.success(
          `Location ${newStatus ? 'archived' : 'unarchived'} successfully`
        );
        setIsArchived(newStatus);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update archive status");
      setIsArchived(!newStatus);
    }
  };

  const formik = useFormik({
    initialValues: {
      // isArchived: false,
      officeName: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "",
      pinCode: "",
      phone: "",
      fax: "",
      website: "",
      email: "",
      bvdqEmail: [""],
      managerFirstName: "",
      managerLastName: "",
      managerEmail: "",
      managerPhone: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",
      ownerPhone: "",
      practiceLogo: null,
      locationStatus: "inActive",
      aboutMe: "",
      medicalInsuranceStatus: false,
      insurance_details: [],
      // locationTag: "",
    },
    validationSchema: stepSixSchema,
    onSubmit: async (values) => {
      values.stateName = stateName;
      // values.cityName = cityName
      try {
        // setEnableFields(true);

        const response = await submitContactInfo(providerId, values);
        // console.log("Response from submitContactInfo:", response);

        if (response.status == false) {
          // console.log("Error in response:", response.message[0]);

          toast.error(response.message[0]);
        } else {
          let obj = {
            event: `SuperAdmin has updated Practice Contact Information for loction ${loc.organization_name}.`,
          };

          await createAuditLogs({ data: encryptData(obj) }, headers);
          toast.success("Information updated successfully");
          setButtonText("Saved");
        }
      } catch (error) {
        //  console.log("Submission Error:", error?.message);
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue } =
    formik;

  // // Get options for countries, states, and cities
  const countries = Country.getAllCountries();
  const states = values.country ? State.getStatesOfCountry(values.country) : [];
  const cities = values.state
    ? City.getCitiesOfState(values.country, values.state)
    : [];

  useEffect(() => {
    if (initialData || status || doctor) {
      formik.setValues({
        officeName: initialData?.practiceNameAndAddress?.officeName || "",
        streetAddress: initialData?.practiceNameAndAddress?.streetAddress || "",
        country: initialData?.practiceNameAndAddress?.country || "",
        state: initialData?.practiceNameAndAddress?.state || "",
        city: initialData?.practiceNameAndAddress?.city || "",
        pinCode: initialData?.practiceNameAndAddress?.pinCode || "",
        phone: initialData?.practiceContactInformation?.phone || "",
        fax: initialData?.practiceContactInformation?.fax || "",
        website: initialData?.practiceContactInformation?.website || "",
        email: initialData?.practiceContactInformation?.email || "",
        bvdqEmail:
          initialData?.practiceContactInformation?.bvdqEmail?.length > 0
            ? initialData.practiceContactInformation.bvdqEmail
            : [status.email],

        managerFirstName:
          initialData?.practiceManagerAdministrator?.firstName || "",
        managerLastName:
          initialData?.practiceManagerAdministrator?.lastName || "",
        managerEmail: initialData?.practiceManagerAdministrator?.email || "",
        managerPhone: initialData?.practiceManagerAdministrator?.phone || "",
        ownerFirstName: initialData?.ownerContactInformation?.firstName || "",
        ownerLastName: initialData?.ownerContactInformation?.lastName || "",
        ownerEmail: initialData?.ownerContactInformation?.email || "",
        ownerPhone: initialData?.ownerContactInformation?.phone || "",
        notes: initialData?.ownerContactInformation?.notes || "",
        practiceLogo: initialData?.practiceLogo || null,
        locationStatus: status.status || "inActive",
        aboutMe: initialData?.locationDetails.aboutMe || "",
        // locationTag: initialData?.locationDetails.locationTag || "",
        medicalInsuranceStatus:
          initialData?.locationDetails?.medicalInsuranceStatus || false,
        insurance_details: doctor?.insurance_details?.length
          ? doctor.insurance_details
          : initialData?.locationDetails?.insurance_details || [],
      });
      if (initialData?.practiceLogo) {
       
        const normalizedFilename = initialData.practiceLogo.replace(/\\/g, "/");
        const imageUrl = `${path}/${normalizedFilename.split("/").pop()}`;

        fetch(imageUrl)
          .then(async (response) => {
            const contentType = response.headers.get("content-type");
            if (!response.ok || !contentType?.startsWith("image/")) {
              throw new Error("Invalid or missing image");
            }

            const blob = await response.blob();
            const file = new File([blob], imageUrl.split("/").pop(), {
              type: blob.type,
            });

            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            setFetchedFilename(imageUrl.split("/").pop());
            setFieldValue("practiceLogo", dataTransfer.files[0]);
          })
          .catch((error) => {
            console.error("Error loading image:", error.message);
            // Optionally show a user-friendly message
          });
      }

      const selectedCountry = countries.find(
        (country) =>
          country.isoCode === initialData?.practiceNameAndAddress?.country
      );

      formik.setFieldValue(
        "country",
        selectedCountry?.isoCode ||
          initialData?.practiceNameAndAddress?.country ||
          ""
      );

      // Find state by name within the selected country and set its isoCode
      const selectedState = selectedCountry
        ? State.getStatesOfCountry(selectedCountry.isoCode).find(
            (state) => state.name === initialData?.practiceNameAndAddress?.state
          )
        : null;

      formik.setFieldValue(
        "state",
        selectedState?.isoCode ||
          initialData?.practiceNameAndAddress?.state ||
          ""
      );
      setStateName(selectedState ? selectedState.name : "");

      // Find city by name within the selected state
      const selectedCity = selectedState
        ? City.getCitiesOfState(
            selectedState.countryCode,
            selectedState.isoCode
          ).find(
            (city) => city.name === initialData?.practiceNameAndAddress?.city
          )
        : null;

      formik.setFieldValue(
        "city",
        selectedCity?.name || initialData?.practiceNameAndAddress?.city || ""
      );
      // Set cityName just like stateName
      setCityName(selectedCity ? selectedCity.name : "");
      setButtonText("Saved");
    }
  }, [initialData]);

  const handleFieldChange = (e) => {
    handleChange(e);
    setButtonText("Save Changes"); // Change button text when a field is modified
  };

  return (
    
    <div className="panel-box">
      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "flex-end",
          marginBottom: "10px",
          alignItems: "center", // vertically center
        }}
      >
        
        <button
         
          className="btn blackoutlineBtn"
          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.96)")}
          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          onClick={() => {
            const baseLink = process.env.REACT_APP_BVDQ_URL;
            const queryParams = new URLSearchParams();

            [
              ["link", status?.website],
              ["locationId", status?.location_id],
              [
                "providerId",
                providerId || status?.practice_id || status?.userId,
              ],
            ].forEach(([key, value]) => {
              if (value) queryParams.append(key, value);
            });

            const webhookLink = `${baseLink}?${queryParams.toString()}`;

            if (providerId || status?.practice_id || status?.userId) {
              navigator.clipboard.writeText(webhookLink);
              toast.success("Webhook URL copied to clipboard!");
            } else {
              toast.error("Provider ID not available.");
            }
          }}
        >
          Copy URL
        </button>

        <button
          className="btn blackoutlineBtn"
          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.96)")}
          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          onClick={() => {
            const id = providerId || status?.practice_id || status?.userId;
            if (id) {
              navigator.clipboard.writeText(id);
              toast.success("Practice ID copied!");
            } else {
              toast.error("Practice ID not available.");
            }
          }}
        >
          Copy Practice ID
        </button>

        <span
          style={{
            fontFamily: "ui-monospace",
            fontSize: "19px",
            marginLeft: "10px",
            marginRight: 0,
            whiteSpace: "nowrap",
            fontWeight: "bold",
          }}
        >
          Location ID&nbsp;:&nbsp;
          <span style={{ fontWeight: "normal" }}>{status?.location_id}</span>
        </span>
      </div>

      <div className="panel-box" style={{ marginBottom: "20px", padding: "15px", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
        <div style={{ marginBottom: "10px" }}>
          <InputLabel shrink>Archived Status</InputLabel>
          <FormControlLabel
            control={
              <Switch
                checked={isArchived}
                onChange={(event) => {
                  const newStatus = event.target.checked;
                  handleArchiveStatusChange(newStatus);
                }}
                color="primary"
                style={{
                  color: isArchived ? "#C31212" : "#42A716",
                }}
              />
            }
            label={isArchived ? "Archived" : "Active"}
          />
        </div>
      </div>
      
      <form className="sectionalForms" onSubmit={handleSubmit}>
        <div className="title"></div>

        <div className="row">
          <div className="subtitle">Practice Name & Address</div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="officeName"
                label="Practice Name"
                className="w-100"
                variant="outlined"
                style={{ backgroundColor: "#F7F7D2" }}
                size="small"
                value={values.officeName}
                onChange={handleFieldChange}
                helperText={touched.officeName && errors.officeName}
                error={touched.officeName && !!errors.officeName}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="streetAddress"
                label="Street Address"
                className="w-100"
                variant="outlined"
                size="small"
                style={{ backgroundColor: "#F7F7D2" }}
                value={values.streetAddress}
                onChange={handleFieldChange}
                helperText={touched.streetAddress && errors.streetAddress}
                error={touched.streetAddress && !!errors.streetAddress}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={cities}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                value={
                  cities.find((c) => c.name === values.city) ||
                  values.city ||
                  ""
                }
                onChange={(event, newValue) => {
                  const cityValue =
                    typeof newValue === "string"
                      ? newValue
                      : newValue?.name || "";
                  setFieldValue("city", cityValue);
                  setCityName(cityValue);
                  setButtonText("Save Changes");
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.name
                      .toLowerCase()
                      .includes(params.inputValue.toLowerCase())
                  );

                  // Add user input as an option if it doesn't match existing cities
                  if (
                    params.inputValue !== "" &&
                    !filtered.some(
                      (option) => option.name === params.inputValue
                    )
                  ) {
                    filtered.push({
                      name: params.inputValue,
                    });
                  }

                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#F7F7D2" }}
                    error={touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                  />
                )}
                freeSolo
                isOptionEqualToValue={(option, value) => option.name === value}
                // disabled={enableField}
              />
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={states}
                getOptionLabel={(option) => option.name}
                value={states.find((s) => s.isoCode === values.state) || null}
                onChange={(event, newValue) => {
                  setFieldValue("state", newValue ? newValue.isoCode : "");
                  setStateName(newValue ? newValue.name : "");
                  setFieldValue("city", ""); // Reset city when state changes
                  setButtonText("Save Changes");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#F7F7D2" }}
                    error={touched.state && !!errors.state}
                    helperText={touched.state && errors.state}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.isoCode === value
                }
                // disabled={enableField}
              />
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="pinCode"
                label="Postal Code"
                style={{ backgroundColor: "#F7F7D2" }}
                className="w-100"
                variant="outlined"
                size="small"
                value={values.pinCode}
                onChange={handleFieldChange}
                helperText={touched.pinCode && errors.pinCode}
                error={touched.pinCode && !!errors.pinCode}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="formdesign">
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name}
                value={
                  countries.find((c) => c.isoCode === formik.values.country) ||
                  null
                }
                onChange={(event, newValue) => {
                  setFieldValue("country", newValue ? newValue.isoCode : "");
                  setFieldValue("state", "");
                  setFieldValue("city", "");
                  setButtonText("Save Changes");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#F7F7D2" }}
                    error={touched.country && !!errors.country}
                    helperText={touched.country && errors.country}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.isoCode === value.isoCode
                }
                // disabled={enableField}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="subtitle">Practice Contact Information</div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <PhoneInput
                defaultCountry="us"
                value={formik.values.phone}
                onChange={(value) => {
                  formik.setFieldValue("phone", value); // value will include +1
                }}
                onBlur={() => formik.setFieldTouched("phone", true)}
                inputClassName="w-full rounded-md px-4 py-2 border text-gray-800 border-gray-300 focus:ring-2 focus:ring-green-500 w-100"
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-red-500 text-sm mt-1">
                  {formik.errors.phone}
                </p>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="fax"
                label="Fax"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.fax}
                onChange={handleFieldChange}
                helperText={touched.fax && errors.fax}
                error={touched.fax && !!errors.fax}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="website"
                label="Website"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.website}
                onChange={handleFieldChange}
                helperText={touched.website && errors.website}
                error={touched.website && !!errors.website}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="email"
                label="Email"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.email}
                onChange={handleFieldChange}
                helperText={touched.email && errors.email}
                error={touched.email && !!errors.email}
                // disabled={true}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            {values.bvdqEmail?.map((email, index) => (
              <div
                className="formdesign mb-2"
                style={{ position: "relative" }}
                key={index}
              >
                <TextField
                  id={`bvdqEmail-${index}`}
                  name={`bvdqEmail[${index}]`}
                  label={`BVDQ Receiver Email`}
                  className="w-100"
                  variant="outlined"
                  size="small"
                  value={email}
                  onChange={(e) => {
                    const emails = [...values.bvdqEmail];
                    emails[index] = e.target.value;
                    setFieldValue("bvdqEmail", emails);
                    setButtonText("Save Changes");
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    touched.bvdqEmail?.[index] && errors.bvdqEmail?.[index]
                  }
                  error={
                    !!(touched.bvdqEmail?.[index] && errors.bvdqEmail?.[index])
                  }
                />

                {/* Remove button (only show if more than one email) */}
                {values.bvdqEmail.length > 1 && (
                  <span
                    style={{
                      position: "absolute",
                      right: 40,
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontSize: "22px",
                      color: "#d32f2f",
                      zIndex: 2,
                    }}
                    onClick={() => {
                      const emails = [...values.bvdqEmail];
                      emails.splice(index, 1);
                      setFieldValue("bvdqEmail", emails);
                      setButtonText("Save Changes");
                    }}
                    title="Remove this email"
                  >
                    &minus;
                  </span>
                )}

                {/* Add button (only on the last input) */}
                {index === values.bvdqEmail.length - 1 && (
                  <span
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontSize: "22px",
                      color: "#1976d2",
                      zIndex: 2,
                    }}
                    onClick={() => {
                      const emails = [...values.bvdqEmail, ""];
                      setFieldValue("bvdqEmail", emails);
                      setButtonText("Save Changes");
                    }}
                    title="Add another email"
                  >
                    +
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="row">
          <div className="subtitle">Practice Logo</div>
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="formdesign">
              <InputLabel shrink>Upload Practice Logo</InputLabel>
              <input
                type="file"
                name="practiceLogo"
                accept="image/*"
                fileName={practiceLogoFilename}
                onChange={(event) => {
                  const file = event.currentTarget.files[0];
                  setFieldValue("practiceLogo", file);
                  // setPracticeLogoFilename(file?.name);
                  setUploadedFilename(file?.name || "");
                  setButtonText("Save Changes");
                }}
                helperText={touched.practiceLogo && errors.practiceLogo}
                error={touched.practiceLogo && !!errors.practiceLogo}
                // disabled={enableField}
              />
              {/* {practiceLogoFilename && <p>{practiceLogoFilename}</p>} */}
              {!uploadedFilename && fetchedFilename && <p>{fetchedFilename}</p>}
              {touched.practiceLogo && errors.practiceLogo && (
                <div
                  className="text-danger"
                  style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
                >
                  {errors.practiceLogo}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="subtitle">Location Page Details</div>
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="formdesign">
              {/* Toggle for Location Status */}
              <div style={{ marginBottom: "20px" }}>
                <InputLabel shrink>Location Status</InputLabel>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.locationStatus === "active"}
                      onChange={(event) => {
                        const status = event.target.checked
                          ? "active"
                          : "inActive";
                        setLocationStatus(status); // Update local state
                        formik.setFieldValue("locationStatus", status); // Use `status` directly
                        setButtonText("Save Changes");
                      }}
                      color="primary"
                      // disabled={enableField}
                      style={{
                        color:
                          values.locationStatus === "active"
                            ? "#42A716"
                            : "#C31212",
                      }}
                    />
                  }
                />
              </div>
              {/* Description Box for About */}
              <div
                style={{
                  marginBottom: "30px",
                  marginTop: "20px",
                }}
              >
                <InputLabel style={{ marginBottom: "10px" }} shrink>
                  About
                </InputLabel>

                <TextField
                  label="Description"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={values.aboutMe}
                  onChange={(event) => {
                    setAbout(event.target.value);
                    formik.setFieldValue("aboutMe", event.target.value);
                    setButtonText("Save Changes");
                  }}
                  fullWidth
                  helperText={touched.aboutMe && errors.aboutMe}
                  error={touched.aboutMe && !!errors.aboutMe}
                  // disabled={enableField}
                />
              </div>
              {/* Toggle for Medical Insurance Status */}
              <div style={{ marginBottom: "30px", marginTop: "30px" }}>
                <InputLabel shrink>Medical Insurance Status</InputLabel>

                <FormControlLabel
                  control={
                    <Switch
                      checked={values.medicalInsuranceStatus === "active"}
                      onChange={(event) => {
                        const status = event.target.checked
                          ? "active"
                          : "inActive";
                        setMedicalInsuranceStatus(status); // Update local state
                        formik.setFieldValue("medicalInsuranceStatus", status); // Use `status` directly
                        setButtonText("Save Changes");
                      }}
                      color="primary"
                      // disabled={enableField}
                      style={{
                        color:
                          values.medicalInsuranceStatus === "active"
                            ? "#42A716"
                            : "#C31212",
                      }}
                    />
                  }
                />
              </div>
              {/* ADD-EDIT_REMOVE In-Network insurance */}
              <div style={{ marginBottom: "10px", marginTop: "20px" }}>
                <InputLabel style={{ marginBottom: "10px" }} shrink>
                  In-Network Insurance
                </InputLabel>
                <Autocomplete
                  multiple
                  sx={{
                    "& .MuiFilledInput-root": {
                      marginTop: "20px",
                      marginBottom: "30px",
                      paddingRight: "39px",
                      backgroundColor: "#fff",
                    },
                  }}
                  id="tags-filled"
                  freeSolo
                  // disabled={enableField}
                  value={values.insurance_details}
                  helperText={
                    touched.insurance_details && errors.insurance_details
                  }
                  error={
                    touched.insurance_details && !!errors.insurance_details
                  }
                  onChange={(event, newValue) => {
                    const formattedValue = newValue;
                    formik.setFieldValue("insurance_details", formattedValue);
                    setButtonText("Save Changes");
                  }}
                  options={insuranceOptions}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Select Networks"
                    />
                  )}
                />
              </div>

            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="text-end">
              <div className="text-end modalactions">
                <button type="submit" className="btn btn-blacked">
                  {buttonText}
                </button>
              </div>
              {/* )} */}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
